import { useCallback, useMemo } from 'react'
import { dateTime, NaiveDateTime, TimeUnits } from '@guestgoodie/helpers'

import Input from '@/components/ui/Input'
import { Trans } from '@lingui/react/macro'
import { HugeiconsIcon } from '@hugeicons/react'
import { PlusSignIcon } from '@hugeicons/core-solid-rounded'

interface DateTimeProps {
  name?: string
  invalid?: boolean
  value?: NaiveDateTime
  onChange: (value: NaiveDateTime) => void
  readOnly?: boolean

  // This can be used to show the DateTimeInput like +X Days, and the user can just select how much he wants to have Offset from the first day
  startDate?: NaiveDateTime
  isOffset?: boolean
}

export default function DateTimeInput({
  invalid = false,
  name,
  value = dateTime.hotelDateNow(),
  isOffset = false,
  readOnly = false,
  startDate,
  onChange
}: DateTimeProps) {
  const onChangeDate = useCallback(
    (date: string) => {
      if (!date) return

      onChange(value.setDate(date))
    },
    [value, onChange]
  )

  const onChangeTime = useCallback(
    (time: string) => {
      const newTime = time ? time : '00:00'

      onChange(value.setTime(newTime))
    },
    [value, onChange]
  )

  const onChangeOffset = useCallback(
    (offset: string) => {
      const offsetNum = parseInt(offset)

      if (!startDate) return
      if (isNaN(offsetNum)) return

      const day = startDate.toDay().add(offsetNum, TimeUnits.DAY)

      onChange(value.setDate(day.getDate()))
    },
    [onChange, startDate, value]
  )

  const offset = useMemo(() => {
    if (!startDate) return

    return value.toDay().diff(startDate.toDay(), TimeUnits.DAY)
  }, [startDate, value])

  const showCounter = isOffset && offset != null

  return (
    <div className="flex flex-row items-center gap-2">
      {showCounter && (
        <div className="flex flex-row gap-1 items-center">
          <HugeiconsIcon icon={PlusSignIcon} className="size-4 text-text" />
          <div className="w-20">
            <Input
              type="number"
              value={offset}
              onChange={event => onChangeOffset(event.target.value)}
              name={`${name} day`}
              invalid={invalid}
              min={0}
              readOnly={readOnly}
            />
          </div>
          <Trans>Days</Trans>
        </div>
      )}
      {!showCounter && (
        <Input
          type="date"
          className="flex-1"
          value={value.getDate()}
          onChange={event => onChangeDate(event.target.value)}
          name={`${name} day`}
          invalid={invalid}
          readOnly={readOnly}
        />
      )}
      <Input
        type="time"
        className="flex-1"
        value={value.getTime()}
        onChange={event => onChangeTime(event.target.value)}
        name={`${name} time`}
        invalid={invalid}
        readOnly={readOnly}
      />
    </div>
  )
}
